import { Box, Container, IconButton, Stack } from '@mui/material';
import { ReactComponent as SmmhubIcon } from 'assets/images/smmhub-dark.svg';
import { ReactComponent as ArrowIcon } from 'assets/images/arrow-long-icon.svg';
import { AuthCookieContext } from 'utils/context';
import { useContext, useRef } from 'react';
import { Transition } from 'react-transition-group';
import { useNavigate } from 'react-router-dom';

const duration = 150;

const defaultStyle = {
  width: '100%',
  height: '100%',
  transition: `opacity ${duration}ms ease-in-out`,
  opacity: 0,
};

const transitionStyles = {
  entering: { opacity: 0, height: 0 },
  entered: { opacity: 1 },
  exiting: { opacity: 0 },
  exited: { opacity: 0 },
};

export default function PageWrapper({ pageName, children }) {
  const { templateConfig } = useContext(AuthCookieContext);

  const quizConfig = templateConfig?.quizConfig;
  const pageConfig = quizConfig?.[pageName];
  const showHeader = pageConfig?.showHeader;
  const showProgressBar = pageConfig?.showProgressBar;
  const progressBarValue = pageConfig?.progressBarValue;

  const suspendedProgressBarValue = useRef(progressBarValue);

  const handleProgressBarTransition = () => {
    suspendedProgressBarValue.current = progressBarValue;
  };

  return (
    <Container
      maxWidth="xs"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        px: 0,
      }}
    >
      <Stack
        sx={{
          position: 'sticky',
          top: 0,
          bgcolor: 'white',
          zIndex: 1000,
        }}
      >
        <Transition
          timeout={duration}
          in={showHeader}
          mountOnEnter
          unmountOnExit
        >
          {(state) => {
            return (
              <Box
                style={{
                  ...defaultStyle,
                  ...transitionStyles[state],
                }}
              >
                <Header />
              </Box>
            );
          }}
        </Transition>
        <Transition
          timeout={duration}
          in={showProgressBar}
          onEntered={handleProgressBarTransition}
          mountOnEnter
          unmountOnExit
        >
          {(state) => {
            return (
              <Box
                style={{
                  ...defaultStyle,
                  ...transitionStyles[state],
                }}
              >
                <ProgressBar
                  value={progressBarValue || suspendedProgressBarValue.current}
                />
              </Box>
            );
          }}
        </Transition>
      </Stack>
      {children}
    </Container>
  );
}

const Header = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '52px',
        display: 'grid',
        gridTemplateColumns: '37px 1fr 37px',
        justifyItems: 'center',
        alignItems: 'center',
      }}
    >
      <IconButton
        size="large"
        onClick={handleBack}
        sx={{
          transform: 'rotate(180deg)',
          '& path': {
            stroke: 'rgba(88, 92, 116, 1)',
          },
        }}
      >
        <ArrowIcon />
      </IconButton>
      <SmmhubIcon />
    </Box>
  );
};

const ProgressBar = ({ value: _value }) => {
  const value = Array.isArray(_value) ? _value : [_value];
  return (
    <Stack direction="row" gap={0.75}>
      {value.map((v, index) => (
        <Box
          key={index}
          sx={{
            width: '100%',
            height: '3px',
            bgcolor: 'rgba(228, 231, 241, 1)',
            borderRadius: '100px',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${v * 100}%`,
              height: '100%',
              borderRadius: '100px',
              background:
                'linear-gradient(90deg, rgba(90, 52, 255, 0.72) 0%, #390BFF 100%)',
              transition: 'width 0.3s ease-in-out',
            }}
          />
        </Box>
      ))}
    </Stack>
  );
};
